import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Tooltip from "@mui/material/Tooltip";
import { RefCallback, useCallback, useEffect, useRef, useState } from "react";
import GenericJsonBuilder, { FieldOption } from "@/components/general/genericJsonBuilder";
import styles from "@/components/dragndrop/dndCollapsible.module.css";
import { useTranslation } from "react-i18next";

interface Props {
  visibilityRuleset?: any;
  mandatory?: boolean;
  onHandleCheckboxChange?: () => void;
  onHandleRulesetChange?: (event: any, id?: any) => void;
  id?: any;
  visibility_ruleset?: any;
  keySuggestions?: { value: string; label?: string }[];
  forceExpanded?: boolean;
  validationTargetRef?: RefCallback<HTMLElement>;
}

export function DnDCollapsible(props: Props) {
  const [ruleset, setRuleset] = useState<any>("");
  const [disableMandatoryField, setDisableMandatoryField] = useState<boolean>(false);
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation();

  const [initialRuleset] = useState(() => {
    if (!props.visibility_ruleset) {
      return { parsed: {}, malformed: false };
    }
    if (typeof props.visibility_ruleset !== "string") {
      return { parsed: props.visibility_ruleset, malformed: false };
    }
    try {
      return { parsed: JSON.parse(props.visibility_ruleset), malformed: false };
    } catch {
      return { parsed: {}, malformed: true };
    }
  });
  const preserveMalformedRulesetRef = useRef(initialRuleset.malformed);

  useEffect(() => {
    if (props.forceExpanded) {
      setExpanded(true);
    }
  }, [props.forceExpanded]);

  useEffect(() => {
    if (props.visibility_ruleset) {
      let visibilityRulesetString: string;

      if (typeof props.visibility_ruleset === "string") {
        visibilityRulesetString = props.visibility_ruleset;
      } else {
        visibilityRulesetString = JSON.stringify(props.visibility_ruleset);
      }

      setRuleset(visibilityRulesetString);
      setDisableMandatoryField(visibilityRulesetString.trim() !== "{}");
    } else {
      setRuleset("{}");
      setDisableMandatoryField(false);
    }
  }, [props.visibility_ruleset]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (props.onHandleRulesetChange) {
        props.onHandleRulesetChange(ruleset, props.id);
      }
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [ruleset]);

  const handleJsonBuilderChange = useCallback((value: any) => {
    const newRulesetString = JSON.stringify(value);
    if (
      preserveMalformedRulesetRef.current &&
      newRulesetString.trim() === "{}"
    ) {
      return;
    }
    preserveMalformedRulesetRef.current = false;
    if (newRulesetString !== ruleset) {
      setRuleset(newRulesetString);

      if (newRulesetString.trim() !== "{}" && props.mandatory && props.onHandleCheckboxChange) {
        props.onHandleCheckboxChange();
      }
    }
  }, [props.mandatory, props.onHandleCheckboxChange, ruleset]);

  const rulesetFieldOptions: FieldOption[] = [
    {
      key: "*",
      type: "string",
      operators: ["is", "not", "gt", "lt", "in", "nin", "contains"],
      renderKeyAs: "textfield",
      allow_custom_input: true,
      renderValueInputTypeToggle: true,
      key_suggestions: props.keySuggestions,
    },
  ];

  return (
    <Accordion
      className={styles.accordion}
      disableGutters={true}
      classes={{ expanded: styles.accordionExpanded }}
      expanded={expanded}
      onChange={(_event, isExpanded) => setExpanded(isExpanded)}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="panel1a-content"
        id="panel1a-header"
        classes={{ expanded: styles.accordionSummaryExpanded }}
      >
        <div className={styles.summaryContent}>
          <span>{t("settings")}</span>
          {disableMandatoryField && (
            <span className={styles.summaryBadge}>
              <VisibilityIcon fontSize="inherit" />
              {t("visibility_ruleset")}
            </span>
          )}
          {props.mandatory && (
            <span className={styles.summaryBadgeMandatory}>
              {t("mandatory_field")}
            </span>
          )}
        </div>
      </AccordionSummary>
      <AccordionDetails>
        <div className={styles.wrapper} ref={props.validationTargetRef}>
          <GenericJsonBuilder
            initialData={initialRuleset.parsed}
            fieldOptions={rulesetFieldOptions}
            renderAs="form"
            enableDragDrop={false}
            enableAddNewEntryButton={true}
            enableResetButton={true}
            enableDeleteButton={true}
            onChange={handleJsonBuilderChange}
            label={t("visibility_ruleset")}
          />
          <Tooltip
            title={
              disableMandatoryField
                ? t("visibility_ruleset_present_cannot_check_box")
                : ""
            }
            arrow
            placement="bottom"
            disableHoverListener={!disableMandatoryField}
          >
            <label className={styles.mandatoryCheckbox}>
              {t("mandatory_field")}
              <input
                type="checkbox"
                checked={props.mandatory}
                disabled={disableMandatoryField}
                onChange={props.onHandleCheckboxChange}
              />
            </label>
          </Tooltip>
        </div>
      </AccordionDetails>
    </Accordion>
  );
}
